import React from 'react';
import { Breadcrumb } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  HomeOutlined,
  DashboardOutlined,
  EditOutlined,
  BlockOutlined,
  HistoryOutlined,
  AppstoreOutlined,
  FileTextOutlined,
  UserOutlined,
  SettingOutlined
} from '@ant-design/icons';

interface BreadcrumbItem {
  href?: string;
  title: React.ReactNode;
  key?: string;
}

const AppBreadcrumb: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Route configuration với icons
  const routeConfig: Record<string, { title: string; icon: React.ReactNode; parent?: string }> = {
    '/': { title: t('navigation.dashboard'), icon: <DashboardOutlined /> },
    '/dashboard': { title: t('navigation.dashboard'), icon: <DashboardOutlined /> },
    '/review': { title: t('navigation.documentReview'), icon: <EditOutlined /> },
    '/chunks': { title: t('navigation.chunkManagement'), icon: <BlockOutlined /> },
    '/history': { title: t('navigation.history'), icon: <HistoryOutlined /> },
    '/azure-search': { title: t('navigation.azureSearch'), icon: <AppstoreOutlined /> },
    '/chat': { title: t('navigation.knowledgeChat'), icon: <FileTextOutlined /> },
    '/users': { title: 'Users', icon: <UserOutlined /> },
    '/settings': { title: t('navigation.settings'), icon: <SettingOutlined /> }
  };

  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const items: BreadcrumbItem[] = [];

    // Luôn bắt đầu với Home
    items.push({
      href: '/',
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <HomeOutlined />
          <span>{t('breadcrumb.home')}</span>
        </div>
      ),
      key: 'home'
    });

    // Nếu đang ở trang chủ thì chỉ hiển thị Home + Dashboard
    if (location.pathname === '/' || location.pathname === '/dashboard') {
      items.push({
        title: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <DashboardOutlined />
            <span>{t('breadcrumb.dashboard')}</span>
          </div>
        ),
        key: 'dashboard'
      });
      return items;
    }

    // Xử lý các route khác
    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const config = routeConfig[currentPath];
      
      if (config) {
        const isLast = index === pathSegments.length - 1;
        
        items.push({
          ...(isLast ? {} : { href: currentPath }),
          title: (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {config.icon}
              <span>{config.title}</span>
            </div>
          ),
          key: currentPath
        });
      } else {
        // Xử lý dynamic routes
        if (currentPath.startsWith('/chunks/') && pathSegments.length === 2) {
          const documentId = pathSegments[1];
          items.push({
            title: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <FileTextOutlined />
                <span>Document Chunks</span>
              </div>
            ),
            key: `chunks-${documentId}`
          });
        } else {
          // Fallback cho unknown routes
          const cleanSegment = segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
          items.push({
            title: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <FileTextOutlined />
                <span>{cleanSegment}</span>
              </div>
            ),
            key: currentPath
          });
        }
      }
    });

    return items;
  };

  const breadcrumbItems = generateBreadcrumbs();

  const handleItemClick = (href: string) => {
    if (href) {
      navigate(href);
    }
  };

  return (
    <Breadcrumb
      style={{ 
        margin: '16px 0',
        padding: '8px 0',
        fontSize: '14px'
      }}
      items={breadcrumbItems.map(item => ({
        ...item,
        onClick: item.href ? () => handleItemClick(item.href!) : undefined,
        style: { 
          ...(item.href ? { 
            cursor: 'pointer',
            color: '#1890ff'
          } : {
            color: '#595959'
          })
        }
      }))}
    />
  );
};

export default AppBreadcrumb;
